import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  ChevronDown,
  Pause,
  Play,
  Eye,
  EyeOff,
  Maximize,
  Minimize,
  Bookmark,
  BookmarkCheck,
  Flag,
  Undo2,
  Info,
  Lightbulb,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Calculator,
  X,
  Bot,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import "katex/dist/katex.min.css";
import { MarkdownRenderer } from "./ui/markdown-renderer";

import {
  loadQuestionsWithSource,
  type Question,
  type QuestionSource,
} from "@/lib/questionLoader";
import { AiraPanel } from "@/components/AiraPanel";
import { recordAnswer, generateSessionId } from "@/lib/progress";
import { hasAiraAccess } from "@/lib/subscriptions";
import { useAuth } from "@/contexts/AuthContext";
import { account } from "@/lib/appwrite";

// Types
// Question type imported from loader

interface TestState {
  currentQuestionIndex: number;
  answers: Record<number, string>;
  markedForReview: Set<number>;
  timeRemaining: number;
  isTimerPaused: boolean;
  showTimer: boolean;
  isFullscreen: boolean;

  showExplanation: boolean;
  showCalculator: boolean;
  checkedAnswers: Set<number>;
}

// Initial state helper
const getInitialState = (questions: Question[]): TestState => ({
  currentQuestionIndex: 0,
  answers: {},
  markedForReview: new Set(),
  timeRemaining: 0,
  isTimerPaused: false,
  showTimer: true,
  isFullscreen: false,
  showExplanation: false,
  showCalculator: false,
  checkedAnswers: new Set(),
});

// Sample questions with LaTeX
const sampleQuestions: Question[] = [
  {
    id: 9,
    passage:
      "A 20 N force pulls a box 5 m across a horizontal floor. The force is applied at an angle of \\(30^{\\circ}\\) above the horizontal.",
    question: "How much work is done by this force?",
    options: [
      { id: "A", text: "50 J", isCorrect: false },
      { id: "B", text: "86.6 J", isCorrect: true },
      { id: "C", text: "100 J", isCorrect: false },
      { id: "D", text: "173.2 J", isCorrect: false },
    ],
    explanation:
      "The work done by a constant force at an angle to the displacement is given by \\(W=Fd~cos(\\theta)\\). Here, \\(F=20~N\\), \\(d=5~m\\), and \\(\\theta=30^{\\circ}\\). So, \\(W=(20~N)(5~m)cos(30^{\\circ})=100~J \\cdot \\frac{\\sqrt{3}}{2} \\approx 86.6~J\\).",
    category: "Physics",
    difficulty: "medium",
  },
  {
    id: 10,
    passage:
      "A 15 N horizontal force is applied to a 2 kg block, moving it 4 m across a rough horizontal surface. The kinetic friction force acting on the block is 5 N.",
    question: "What is the net work done on the block?",
    options: [
      { id: "A", text: "20 J", isCorrect: false },
      { id: "B", text: "40 J", isCorrect: true },
      { id: "C", text: "60 J", isCorrect: false },
      { id: "D", text: "80 J", isCorrect: false },
    ],
    explanation:
      "Work by applied force: \\(W_{applied}=(15~N)(4~m)=60~J\\). Work by friction: \\(W_{friction}=(5~N)(4~m)cos(180^{\\circ})=-20~J\\). Net work: \\(W_{net}=60~J-20~J=40~J\\).",
    category: "Physics",
    difficulty: "medium",
  },
  {
    id: 11,
    passage:
      "A variable force \\(F(x)=(3x^{2}+2x)\\) N acts on an object, moving it from \\(x=1\\) m to \\(x=3\\) m.",
    question: "Calculate the work done by this force.",
    options: [
      { id: "A", text: "24 J", isCorrect: false },
      { id: "B", text: "30 J", isCorrect: false },
      { id: "C", text: "34 J", isCorrect: true },
      { id: "D", text: "38 J", isCorrect: false },
    ],
    explanation:
      "Work is given by \\(W=\\int_{1}^{3}(3x^{2}+2x)dx = [x^{3}+x^{2}]_{1}^{3}\\). Evaluating: \\(((3)^{3}+(3)^{2}) - ((1)^{3}+(1)^{2}) = (27+9) - (1+1) = 36-2 = 34~J\\).",
    category: "Physics",
    difficulty: "hard",
  },
  {
    id: 12,
    passage:
      "A particle moves along the x-axis under the influence of a force \\(F(x)=(6x-2)\\) N from \\(x=0\\) m to \\(x=4\\) m.",
    question: "What is the total work done by this force?",
    options: [
      { id: "A", text: "24 J", isCorrect: false },
      { id: "B", text: "32 J", isCorrect: false },
      { id: "C", text: "40 J", isCorrect: true },
      { id: "D", text: "48 J", isCorrect: false },
    ],
    explanation:
      "Work is given by \\(W=\\int_{0}^{4}(6x-2)dx = [3x^{2}-2x]_{0}^{4}\\). Evaluating: \\((3(4)^{2}-2(4)) - (0) = (48-8) = 40~J\\).",
    category: "Physics",
    difficulty: "hard",
  },
  {
    id: 13,
    passage: "A constant net force of 500 N acts on a 1,000-kg car.",
    question: "What is the magnitude of the car's acceleration?",
    options: [
      { id: "A", text: "\\(0.5~m/s^{2}\\)", isCorrect: true },
      { id: "B", text: "\\(1.0~m/s^{2}\\)", isCorrect: false },
      { id: "C", text: "\\(2.0~m/s^{2}\\)", isCorrect: false },
      { id: "D", text: "\\(5.0~m/s^{2}\\)", isCorrect: false },
    ],
    explanation:
      "According to Newton's Second Law, \\(a=\\frac{F_{net}}{m}\\). With \\(F_{net}=500~N\\) and \\(m=1,000~kg\\), \\(a=\\frac{500~N}{1,000~kg}=0.5~m/s^{2}\\).",
    category: "Physics",
    difficulty: "easy",
  },
  {
    id: 14,
    passage: "A constant net force of 500 N moves a 1,000-kg car 100 m.",
    question: "How much work is done by the net force?",
    options: [
      { id: "A", text: "500 J", isCorrect: false },
      { id: "B", text: "5,000 J", isCorrect: false },
      { id: "C", text: "50,000 J", isCorrect: true },
      { id: "D", text: "500,000 J", isCorrect: false },
    ],
    explanation:
      "Work done by a constant force is \\(W=Fd\\). Given \\(F=500~N\\) and \\(d=100~m\\), \\(W=(500~N)(100~m)=50,000~J\\).",
    category: "Physics",
    difficulty: "easy",
  },
  {
    id: 15,
    passage:
      "A 20-kg block is initially moving at \\(2~m/s\\) on a frictionless horizontal surface. A constant net force of 60 N acts on it in the direction of motion, increasing its speed to \\(8~m/s\\).",
    question: "How far did the block travel?",
    options: [
      { id: "A", text: "5 m", isCorrect: false },
      { id: "B", text: "10 m", isCorrect: true },
      { id: "C", text: "15 m", isCorrect: false },
      { id: "D", text: "20 m", isCorrect: false },
    ],
    explanation:
      "Acceleration \\(a=\\frac{60~N}{20~kg}=3~m/s^{2}\\). Using \\(v_{f}^{2}=v_{0}^{2}+2ad\\): \\(8^{2}=2^{2}+2(3)d \\Rightarrow 64=4+6d \\Rightarrow 60=6d \\Rightarrow d=10~m\\).",
    category: "Physics",
    difficulty: "medium",
  },
  {
    id: 16,
    passage:
      "A 10-kg box is pulled by a horizontal force of 50 N. The coefficient of kinetic friction is 0.2. The box starts from rest.",
    question:
      "What is its speed after it has moved 4 m? (Assume \\(g=10~m/s^{2}\\))",
    options: [
      { id: "A", text: "3.5 m/s", isCorrect: false },
      { id: "B", text: "4.0 m/s", isCorrect: false },
      { id: "C", text: "4.9 m/s", isCorrect: true },
      { id: "D", text: "5.5 m/s", isCorrect: false },
    ],
    explanation:
      "Friction \\(f_{k}=(0.2)(10)(10)=20~N\\). Net force \\(F_{net}=50-20=30~N\\). Acceleration \\(a=\\frac{30}{10}=3~m/s^{2}\\). Velocity \\(v_{f}^{2}=0^{2}+2(3)(4)=24 \\Rightarrow v_{f}=\\sqrt{24} \\approx 4.9~m/s\\).",
    category: "Physics",
    difficulty: "medium",
  },
  {
    id: 17,
    passage:
      "A 2-kg particle starts from rest at \\(x=0\\). A force \\(F(x)=(6x^{2}+2x)\\) N acts on it.",
    question: "What is the particle's speed when it reaches \\(x=2\\) m?",
    options: [
      { id: "A", text: "\\(2~m/s\\)", isCorrect: false },
      { id: "B", text: "\\(\\sqrt{10}~m/s\\)", isCorrect: false },
      { id: "C", text: "\\(2\\sqrt{5}~m/s\\)", isCorrect: true },
      { id: "D", text: "\\(5~m/s\\)", isCorrect: false },
    ],
    explanation:
      "Work \\(W=\\int_{0}^{2}(6x^{2}+2x)dx = [2x^{3}+x^{2}]_{0}^{2} = (16+4) = 20~J\\). Using \\(W=\\frac{1}{2}mv_{f}^{2}\\): \\(20=\\frac{1}{2}(2)v_{f}^{2} \\Rightarrow v_{f}^{2}=20 \\Rightarrow v_{f}=2\\sqrt{5}~m/s\\).",
    category: "Physics",
    difficulty: "hard",
  },
  {
    id: 18,
    passage:
      "A 0.5-kg object is initially moving at 2 m/s. A net force \\(F(t)=(3t^{2})\\) N acts on it for 2 seconds.",
    question: "What is the object's final velocity?",
    options: [
      { id: "A", text: "10 m/s", isCorrect: false },
      { id: "B", text: "14 m/s", isCorrect: false },
      { id: "C", text: "18 m/s", isCorrect: true },
      { id: "D", text: "20 m/s", isCorrect: false },
    ],
    explanation:
      "Impulse \\(J=\\int_{0}^{2}3t^{2}dt = [t^{3}]_{0}^{2} = 8~N \\cdot s\\). Using \\(J=mv_{f}-mv_{0}\\): \\(8=0.5v_{f}-(0.5)(2) \\Rightarrow 8=0.5v_{f}-1 \\Rightarrow 9=0.5v_{f} \\Rightarrow v_{f}=18~m/s\\).",
    category: "Physics",
    difficulty: "hard",
  },
  {
    id: 19,
    passage:
      "A small 2.0 kg box is lifted vertically by 1.0 m from the ground. Assume \\(g=10~m/s^{2}\\).",
    question: "What is the change in the box's gravitational potential energy?",
    options: [
      { id: "A", text: "5.0 J", isCorrect: false },
      { id: "B", text: "10 J", isCorrect: false },
      { id: "C", text: "20 J", isCorrect: true },
      { id: "D", text: "40 J", isCorrect: false },
    ],
    explanation:
      "Change in potential energy is \\(\\Delta U_{g}=mgh = (2.0~kg)(10~m/s^{2})(1.0~m) = 20~J\\).",
    category: "Physics",
    difficulty: "easy",
  },
  {
    id: 20,
    passage:
      "Which of the following factors primarily determine the gravitational potential energy of an object near Earth's surface?",
    question: "Which factors determine gravitational potential energy?",
    options: [
      { id: "A", text: "Its mass and its speed.", isCorrect: false },
      { id: "B", text: "Its mass and its height.", isCorrect: true },
      { id: "C", text: "Its speed and its acceleration.", isCorrect: false },
      {
        id: "D",
        text: "Its mass, its speed, and its height.",
        isCorrect: false,
      },
    ],
    explanation:
      "The formula \\(U_{g}=mgh\\) shows that gravitational potential energy depends on mass (m) and height (h).",
    category: "Physics",
    difficulty: "easy",
  },
];

// LaTeX Renderer Component

// Desmos Calculator Component
function DesmosCalculator({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".calculator-header")) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-50 shadow-2xl rounded-lg overflow-hidden border border-border bg-background"
      style={{
        left: position.x,
        top: position.y,
        width: 400,
        height: 500,
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="calculator-header flex items-center justify-between px-3 py-2 bg-muted cursor-move border-b border-border">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          <span className="font-medium text-sm">Graphing Calculator</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <iframe
        ref={iframeRef}
        src="https://www.desmos.com/calculator"
        className="w-full h-[calc(100%-40px)]"
        title="Desmos Calculator"
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
}

// Question Navigator Popover Content
function NavigatorContent({
  questions,
  currentIndex,
  answers,
  markedForReview,
  checkedAnswers,
  onNavigate,
  onClose,
}: {
  questions: Question[];
  currentIndex: number;
  answers: Record<number, string>;
  markedForReview: Set<number>;
  checkedAnswers: Set<number>;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  return (
    <div className="w-80">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Questions
          </p>
          <p className="text-sm font-semibold">
            {currentIndex + 1} / {questions.length}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-6 gap-2 mb-3">
        {questions.map((question, index) => {
          const questionNum = index + 1;
          const isAnswered = answers[questionNum] !== undefined;
          const isMarked = markedForReview.has(questionNum);
          const isChecked = checkedAnswers.has(questionNum);
          const isCurrent = index === currentIndex;

          if (!question) return null;

          return (
            <button
              key={index}
              onClick={() => {
                onNavigate(index);
                onClose();
              }}
              className={cn(
                "w-10 h-10 rounded-lg font-medium text-sm transition-all relative border border-border hover:border-primary/60",
                isCurrent && "ring-2 ring-primary",
                isChecked && isAnswered
                  ? question.options.find((o) => o.id === answers[questionNum])
                      ?.isCorrect
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-red-100 text-red-800 border border-red-200"
                  : isAnswered
                  ? "bg-primary/10 text-foreground"
                  : "bg-background"
              )}
            >
              {questionNum}
              {isMarked && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-primary/20 border border-primary/30" />
          Answered
        </div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-background border border-border" />
          Unanswered
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          Marked
        </div>
      </div>
    </div>
  );
}
// Explanation Panel Component
function ExplanationPanel({
  isOpen,
  question,
  selectedAnswer,
  onClose,
  onAskAira,
}: {
  isOpen: boolean;
  question: Question;
  selectedAnswer?: string;
  onClose: () => void;
  onAskAira: () => void;
}) {
  if (!isOpen) return null;

  const selectedOption = question.options.find((o) => o.id === selectedAnswer);
  const isCorrect = selectedOption?.isCorrect;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto p-6 pl-16">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                isCorrect
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : "bg-red-100 text-red-800 border border-red-200"
              )}
            >
              {isCorrect ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <X className="h-5 w-5" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-lg">
                {isCorrect ? "Correct!" : "Incorrect"}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isCorrect
                  ? "Great job!"
                  : `The correct answer is ${
                      question.options.find((o) => o.isCorrect)?.id
                    }`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-violet-600 border-violet-200 hover:bg-violet-50 hover:text-violet-700"
              onClick={onAskAira}
            >
              <Bot className="h-4 w-4" />
              Ask Aira
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-yellow-500" />
            Explanation
          </h4>
          <MarkdownRenderer content={question.explanation} />
        </div>
      </div>
    </div>
  );
}

const SESSION_SIZE = 10;


// Main Practice Test Component
export function PracticeTest() {
  const { user, loading: authLoading, subscriptions, stats, updateStats } = useAuth();
  const sessionId = useRef(generateSessionId()).current;
  const sessionStarted = useRef(false);

  // Parse URL params once — available to all handlers
  const urlParams = useRef(new URLSearchParams(window.location.search));
  const subjectId   = urlParams.current.get("subject")    || "ap-physics-1";
  const moduleId    = urlParams.current.get("module")     || "ap_physics_1";
  const topicId     = urlParams.current.get("topic")      || "general";
  const subtopicId  = urlParams.current.get("subtopic")   || undefined;
  const difficulty  = (urlParams.current.get("difficulty") || undefined) as "easy"|"medium"|"hard"|undefined;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [questionSource, setQuestionSource] = useState<QuestionSource | null>(
    null
  );
  // UI State
  const [showAira, setShowAira] = useState(false);
  const [sidebarNavigatorOpen, setSidebarNavigatorOpen] = useState(false);
  const [footerNavigatorOpen, setFooterNavigatorOpen] = useState(false);
  const [showDirections, setShowDirections] = useState(false);
  const [dividerPosition, setDividerPosition] = useState(50);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<TestState>(getInitialState([]));

  // Load questions effect — waits for auth to resolve so we can personalise selection
  useEffect(() => {
    if (authLoading) return;
    if (sessionStarted.current) return;
    sessionStarted.current = true;

    const loadData = async () => {
      setLoading(true);

      if (!user) {
        // Unauthenticated: show first 10 questions, no personalization
        const { questions: allQuestions, source } = await loadQuestionsWithSource(subjectId, moduleId, {
          topicId:    topicId !== "general" ? topicId    : undefined,
          subtopicId: subtopicId                         || undefined,
          difficulty: difficulty                         ?? undefined,
        });
        const preview = allQuestions.slice(0, SESSION_SIZE);
        setQuestions(preview);
        setState(getInitialState(preview));
        setQuestionSource(source);
        setLoading(false);
        return;
      }

      // Authenticated: server enforces subscription gate + session limit + smart selection
      try {
        const { jwt } = await account.createJWT();
        const res = await fetch("/api/session/start", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            subjectId,
            moduleId,
            topicId:    topicId !== "general" ? topicId    : undefined,
            subtopicId: subtopicId                         || undefined,
            difficulty: difficulty                         ?? undefined,
          }),
        });

        if (res.status === 403) {
          const data = await res.json().catch(() => ({}));
          if ((data as any).error === "session_limit_reached") {
            window.location.href = "/paywall";
            return;
          }
        }

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const { questions: selected } = await res.json() as { questions: Question[]; isPaid: boolean };
        setQuestions(selected);
        setState(getInitialState(selected));
        setQuestionSource("database");
        setLoading(false);
      } catch (err) {
        console.error("Session start failed:", err);
        setLoading(false);
      }
    };
    loadData();
  }, [authLoading]);

  // Timer effect - must be before early returns
  useEffect(() => {
    if (loading || questions.length === 0) return;
    if (state.isTimerPaused) return;

    const interval = setInterval(() => {
      setState((prev) => ({
        ...prev,
        timeRemaining: prev.timeRemaining + 1,
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [loading, questions.length, state.isTimerPaused, state.timeRemaining]);

  // Keyboard shortcuts - must be before early returns
  useEffect(() => {
    if (loading || questions.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;

      switch (e.key.toUpperCase()) {
        case "A":
        case "B":
        case "C":
        case "D":
          // Will be handled by component
          break;
        case "ARROWLEFT":
          if (state.currentQuestionIndex > 0) {
            setState((prev) => ({
              ...prev,
              currentQuestionIndex: prev.currentQuestionIndex - 1,
              showExplanation: false,
            }));
          }
          break;
        case "ARROWRIGHT":
          if (state.currentQuestionIndex < questions.length - 1) {
            setState((prev) => ({
              ...prev,
              currentQuestionIndex: prev.currentQuestionIndex + 1,
              showExplanation: false,
            }));
          }
          break;
        case "M":
          setState((prev) => {
            const newMarked = new Set(prev.markedForReview);
            const qNum = prev.currentQuestionIndex + 1;
            if (newMarked.has(qNum)) {
              newMarked.delete(qNum);
            } else {
              newMarked.add(qNum);
            }
            return { ...prev, markedForReview: newMarked };
          });
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [loading, questions.length, state.currentQuestionIndex]);

  // Fullscreen effect - must be before early returns
  useEffect(() => {
    const handleFullscreenChange = () => {
      setState((prev) => ({
        ...prev,
        isFullscreen: !!document.fullscreenElement,
      }));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Divider drag handling - must be before early returns
  const handleDividerMouseDown = useCallback(() => {
    setIsDraggingDivider(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingDivider || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newPosition =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;
      setDividerPosition(Math.min(Math.max(newPosition, 20), 80));
    };

    const handleMouseUp = () => {
      setIsDraggingDivider(false);
    };

    if (isDraggingDivider) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingDivider]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        Loading questions...
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-bold">
          No questions found for this module.
        </h2>
        <Button onClick={() => (window.location.href = "/subjects")}>
          Back to Subjects
        </Button>
      </div>
    );
  }

  const totalQuestions = questions.length;

  const safeIndex = Math.min(
    Math.max(state.currentQuestionIndex, 0),
    totalQuestions - 1
  );
  const currentQuestion: Question = questions[safeIndex]!;
  const questionNumber = safeIndex + 1;

  // Handlers
  const handleSelectAnswer = (answerId: string) => {
    setState((prev) => {
      const newAnswers = { ...prev.answers, [questionNumber]: answerId };
      const newChecked = new Set(prev.checkedAnswers);
      // If an answer is selected, remove it from checkedAnswers if it was previously checked
      if (newChecked.has(questionNumber)) {
        newChecked.delete(questionNumber);
      }
      return {
        ...prev,
        answers: newAnswers,
        showExplanation: false,
        checkedAnswers: newChecked,
      };
    });
  };

  const handleNext = () => {
    if (state.currentQuestionIndex < questions.length - 1) {
      setState((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
        showExplanation: false,
      }));
    }
  };

  const handlePrevious = () => {
    if (state.currentQuestionIndex > 0) {
      setState((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex - 1,
        showExplanation: false,
      }));
    }
  };

  const handleToggleMarkForReview = () => {
    setState((prev) => {
      const newMarked = new Set(prev.markedForReview);
      if (newMarked.has(questionNumber)) {
        newMarked.delete(questionNumber);
      } else {
        newMarked.add(questionNumber);
      }
      return { ...prev, markedForReview: newMarked };
    });
  };

  const handleCheckAnswer = () => {
    const selected = state.answers[questionNumber];
    if (!selected) return;

    // Don't double-record if already checked
    if (!state.checkedAnswers.has(questionNumber)) {
      const isCorrect = currentQuestion.options.find(o => o.id === selected)?.isCorrect ?? false;
      const timeSpent = state.timeRemaining;

      if (user) {
        recordAnswer({
          userId: user.$id,
          subjectId,
          moduleId,
          topicId,
          questionId: String(currentQuestion.id),
          selectedAnswer: selected,
          isCorrect,
          timeSpent,
          sessionId,
        }, stats).then(updateStats).catch(console.warn);
      }
    }

    setState((prev) => ({
      ...prev,
      checkedAnswers: new Set([...prev.checkedAnswers, questionNumber]),
      showExplanation: true,
    }));
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isMarkedForReview = state.markedForReview.has(questionNumber);
  const selectedAnswer = state.answers[questionNumber];
  const isChecked = state.checkedAnswers.has(questionNumber);
  const answeredCount = Object.keys(state.answers).length;
  const markedCount = state.markedForReview.size;
  const airaEnabled = hasAiraAccess(subscriptions);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Navbar */}
      <header className="h-14 border-b border-border flex items-center justify-between px-4 bg-background sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-semibold text-lg">Practice Test</h1>
            <div className="flex items-center gap-2">
              <button
                className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground transition-colors"
                onClick={() => setShowDirections(!showDirections)}
              >
                Directions
                <ChevronDown className="h-3 w-3" />
              </button>
              {questionSource && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground">
                  {questionSource === "database"
                    ? "Using database questions"
                    : "Using built-in demo questions"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Timer controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  isTimerPaused: !prev.isTimerPaused,
                }))
              }
            >
              {state.isTimerPaused ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() =>
                setState((prev) => ({ ...prev, showTimer: !prev.showTimer }))
              }
            >
              {state.showTimer ? (
                <>
                  <Eye className="h-4 w-4" />
                  <span className="font-mono">
                    {formatTime(state.timeRemaining)}
                  </span>
                </>
              ) : (
                <>
                  <EyeOff className="h-4 w-4" />
                  Show
                </>
              )}
            </Button>
          </div>

          {/* Sidebar toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarHidden((prev) => !prev)}
            title={sidebarHidden ? "Show Sidebar" : "Hide Sidebar"}
          >
            {sidebarHidden ? (
              <PanelLeft className="h-5 w-5" />
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </Button>

          {/* Quick actions */}
          <div className="flex items-center gap-2 pl-3 border-l border-border">
            <Button
              variant={state.showCalculator ? "default" : "ghost"}
              size="icon"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  showCalculator: !prev.showCalculator,
                }))
              }
              title="Calculator (C)"
            >
              <Calculator className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleFullscreen}
              title="Fullscreen"
            >
              {state.isFullscreen ? (
                <Minimize className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside
          className={cn(
            "border-r border-border bg-muted/30 p-4 flex flex-col gap-4 overflow-auto transition-all duration-300 ease-in-out",
            sidebarHidden
              ? "w-0 p-0 opacity-0 overflow-hidden"
              : "w-64 opacity-100"
          )}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">
                Section
              </p>
              <p className="font-semibold capitalize">
                {subtopicId
                  ? subtopicId.replace(/_/g, " ")
                  : topicId.replace(/_/g, " ")}
                {difficulty && (
                  <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full ${
                    difficulty === "easy"   ? "bg-green-100 text-green-700"  :
                    difficulty === "medium" ? "bg-yellow-100 text-yellow-700" :
                                             "bg-red-100 text-red-700"
                  }`}>{difficulty}</span>
                )}
              </p>
            </div>
            <Popover
              open={sidebarNavigatorOpen}
              onOpenChange={setSidebarNavigatorOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Open navigator"
                  aria-label="Open question navigator"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                className="p-4 shadow-xl border-border"
              >
                <NavigatorContent
                  questions={questions}
                  currentIndex={safeIndex}
                  answers={state.answers}
                  markedForReview={state.markedForReview}
                  checkedAnswers={state.checkedAnswers}
                  onNavigate={(index) =>
                    setState((prev) => ({
                      ...prev,
                      currentQuestionIndex: index,
                      showExplanation: false,
                    }))
                  }
                  onClose={() => setSidebarNavigatorOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="rounded-xl border border-border bg-background p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Time Elapsed</span>
              <span className="font-mono font-semibold">
                {state.showTimer ? formatTime(state.timeRemaining) : "—:——"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Answered</span>
              <span className="font-medium">
                {answeredCount}/{totalQuestions}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Marked</span>
              <span className="font-medium">{markedCount}</span>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    isTimerPaused: !prev.isTimerPaused,
                  }))
                }
              >
                {state.isTimerPaused ? "Resume" : "Pause"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  setState((prev) => ({ ...prev, showTimer: !prev.showTimer }))
                }
              >
                {state.showTimer ? "Hide" : "Show"} Time
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Questions</h3>
              <span className="text-xs text-muted-foreground">
                {questionNumber} / {totalQuestions}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {questions.map((question, idx) => {
                const num = idx + 1;
                const answered = state.answers[num] !== undefined;
                const marked = state.markedForReview.has(num);
                const checked = state.checkedAnswers.has(num);

                return (
                  <button
                    key={question.id}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        currentQuestionIndex: idx,
                        showExplanation: false,
                      }))
                    }
                    className={cn(
                      "relative h-10 rounded-lg text-sm font-medium transition-all border border-border hover:border-primary/60",
                      idx === state.currentQuestionIndex &&
                        "ring-2 ring-primary",
                      checked && answered
                        ? question.options.find(
                            (o) => o.id === state.answers[num]
                          )?.isCorrect
                          ? "bg-green-100 text-green-800 border border-green-200"
                          : "bg-red-100 text-red-800 border border-red-200"
                        : answered
                        ? "bg-primary/10 text-foreground"
                        : "bg-background"
                    )}
                  >
                    {num}
                    {marked && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Quick actions</h3>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleMarkForReview}
                className="justify-start gap-2"
              >
                {isMarkedForReview ? (
                  <BookmarkCheck className="h-4 w-4" />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
                {isMarkedForReview ? "Unmark for Review" : "Mark for Review"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setState((prev) => ({ ...prev, showCalculator: true }))
                }
                className="justify-start gap-2"
              >
                <Calculator className="h-4 w-4" />
                Open Calculator
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Column */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Directions Dropdown */}
          {showDirections && (
            <div className="bg-muted border-b border-border p-4">
              <div className="max-w-4xl mx-auto">
                <h3 className="font-semibold mb-2">Directions</h3>
                <p className="text-sm text-muted-foreground">
                  The questions in this section address a number of important
                  reading and writing skills. Each question includes one or more
                  passages, which may include a table or graph. Read each
                  passage and question carefully, and then choose the best
                  answer to the question based on the passage(s).
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  All questions in this section are multiple-choice with four
                  answer choices. Each question has a single best answer.
                </p>
              </div>
            </div>
          )}

          {/* Main Content - Split View */}
          <div
            ref={containerRef}
            className="flex-1 flex relative overflow-hidden min-h-0"
            style={{ cursor: isDraggingDivider ? "col-resize" : undefined }}
          >
            {/* Left Panel - Passage */}
            <div
              className="h-full overflow-auto border-r border-border"
              style={{ width: `${dividerPosition}%` }}
            >
              <div className="p-8 max-w-prose">
                {currentQuestion.passageImage && (
                  <img
                    src={currentQuestion.passageImage}
                    alt="Passage illustration"
                    className="w-full rounded-lg mb-4"
                  />
                )}
                {currentQuestion.passage && (
                  <div className="text-lg leading-relaxed prose prose-neutral">
                    <MarkdownRenderer content={currentQuestion.passage} />
                  </div>
                )}
              </div>
            </div>

            {/* Resizable Divider */}
            <div
              className="w-1.5 bg-border hover:bg-primary/20 cursor-col-resize flex items-center justify-center transition-colors relative z-10"
              onMouseDown={handleDividerMouseDown}
            >
              <div className="w-1 h-8 rounded-full bg-muted-foreground/30" />
            </div>

            {/* Right Panel - Question */}
            <div
              className="h-full overflow-auto"
              style={{ width: `${100 - dividerPosition}%` }}
            >
              <div className="p-6">
                {/* Question Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-foreground text-background rounded-lg flex items-center justify-center font-semibold">
                      {questionNumber}
                    </div>
                    <button
                      className={cn(
                        "flex items-center gap-2 text-sm font-medium transition-colors",
                        isMarkedForReview
                          ? "text-orange-500"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={handleToggleMarkForReview}
                      title="Mark for Review (M)"
                    >
                      {isMarkedForReview ? (
                        <BookmarkCheck className="h-4 w-4" />
                      ) : (
                        <Bookmark className="h-4 w-4" />
                      )}
                      Mark for Review
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                      <Flag className="h-4 w-4" />
                      Report
                    </button>
                    <button className="text-muted-foreground hover:text-foreground transition-colors">
                      <Undo2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Question Text */}
                <div className="mb-6">
                  {currentQuestion.questionImage && (
                    <img
                      src={currentQuestion.questionImage}
                      alt="Question illustration"
                      className="w-full max-w-md rounded-lg mb-4"
                    />
                  )}
                  <div className="text-lg">
                    <MarkdownRenderer content={currentQuestion.question} />
                  </div>
                </div>

                {/* Answer Options */}
                <div className="space-y-3">
                  {currentQuestion.options.map((option) => {
                    const isSelected = selectedAnswer === option.id;
                    const showResult = isChecked && isSelected;

                    return (
                      <button
                        key={option.id}
                        onClick={() => handleSelectAnswer(option.id)}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all",
                          isSelected &&
                            !isChecked &&
                            "border-primary bg-primary/5",
                          !isSelected &&
                            "border-border hover:border-muted-foreground/50",
                          showResult &&
                            option.isCorrect &&
                            "border-green-200 bg-green-50 text-green-800",
                          showResult &&
                            !option.isCorrect &&
                            "border-red-200 bg-red-50 text-red-800",
                          isChecked &&
                            option.isCorrect &&
                            !isSelected &&
                            "border-green-200 bg-green-50/80 text-green-800"
                        )}
                      >
                        <div
                          className={cn(
                            "w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold text-sm shrink-0 transition-colors",
                            isSelected &&
                              !isChecked &&
                              "border-primary bg-primary text-primary-foreground",
                            !isSelected && "border-foreground",
                            showResult &&
                              option.isCorrect &&
                              "border-green-300 bg-green-100 text-green-900",
                            showResult &&
                              !option.isCorrect &&
                              "border-red-300 bg-red-100 text-red-900",
                            isChecked &&
                              option.isCorrect &&
                              !isSelected &&
                              "border-green-300 bg-green-100 text-green-900"
                          )}
                        >
                          {option.id}
                        </div>
                        <span className="flex-1">
                          <MarkdownRenderer content={option.text} />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Navigation Bar */}
          <footer className="h-16 border-t border-border flex items-center justify-between px-4 bg-background sticky bottom-0 z-30">
            <Popover
              open={footerNavigatorOpen}
              onOpenChange={setFooterNavigatorOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2 bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  aria-label="Open question navigator"
                >
                  {questionNumber} of {questions.length}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="top"
                className="p-4 shadow-xl border-border"
              >
                <NavigatorContent
                  questions={questions}
                  currentIndex={safeIndex}
                  answers={state.answers}
                  markedForReview={state.markedForReview}
                  checkedAnswers={state.checkedAnswers}
                  onNavigate={(index) =>
                    setState((prev) => ({
                      ...prev,
                      currentQuestionIndex: index,
                      showExplanation: false,
                    }))
                  }
                  onClose={() => setFooterNavigatorOpen(false)}
                />
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" title="Info">
                <Info className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                className="gap-2 "
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    showExplanation: !prev.showExplanation,
                  }))
                }
              >
                <Lightbulb className="h-4 w-4" />
                Explanation
              </Button>

              {airaEnabled ? (
                <Button
                  variant={showAira ? "default" : "ghost"}
                  className={cn(
                    "gap-2",
                    !showAira && "text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                  )}
                  onClick={() => setShowAira((prev) => !prev)}
                >
                  <Bot className="h-4 w-4" />
                  Ask Aira
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="gap-2 text-muted-foreground"
                  onClick={() => (window.location.href = "/paywall")}
                  title="Upgrade to Pro for Aira AI tutoring"
                >
                  <Bot className="h-4 w-4" />
                  Ask Aira ✦
                </Button>
              )}

              <Button
                variant="ghost"
                className="gap-2  "
                onClick={handleCheckAnswer}
                disabled={!selectedAnswer}
              >
                <CheckCircle2 className="h-4 w-4" />
                Check
              </Button>

              <div className="flex items-center border-l border-border pl-3 ml-2 gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrevious}
                  disabled={state.currentQuestionIndex === 0}
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNext}
                  disabled={state.currentQuestionIndex === questions.length - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          </footer>

          {/* Progress Bar */}
          <div className="w-full h-1 bg-border/20">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${
                  ((state.currentQuestionIndex + 1) / questions.length) * 100
                }%`,
              }}
            />
          </div>
          {/* SAT Disclaimer */}
          <div className="text-center py-2 text-xs text-muted-foreground bg-muted/30">
            All questions are created by verified experts.
          </div>
        </div>
      </div>

      {/* Desmos Calculator */}
      <DesmosCalculator
        isOpen={state.showCalculator}
        onClose={() => setState((prev) => ({ ...prev, showCalculator: false }))}
      />

      {/* Explanation Panel */}
      {state.showExplanation && selectedAnswer && currentQuestion && (
        <ExplanationPanel
          isOpen={state.showExplanation}
          question={currentQuestion}
          selectedAnswer={selectedAnswer}
          onClose={() => setState((prev) => ({ ...prev, showExplanation: false }))}
          onAskAira={() => setShowAira(true)}
        />
      )}

      {/* Aira AI Tutor Panel */}
      {airaEnabled && (
        <AiraPanel
          isOpen={showAira}
          onClose={() => setShowAira(false)}
          topic={topicId}
          question={currentQuestion}
          selectedAnswer={selectedAnswer}
          isChecked={isChecked}
        />
      )}
    </div>
  );
}

export default PracticeTest;
